import { AxiosError } from "axios";
import { httpTokenCommon } from "../helper/http-common";
import {
  approvalStatusName,
  computeOrderAmount,
  buildCustomerOutstandingSummaries,
  enrichCodOrders,
  enrichCodPayments,
  EnrichedCodPayment,
  isCodRouteMissingStatus,
  isFullyPaidCodOrder,
  isInvoiceNotIssuedError,
  isPostableCodOrder,
  normalizeOutstandingApiPayload,
  OutstandingApiResult,
  parseSageOrderNumbers,
  pickField,
  unwrapListPayload,
} from "../utils/codUtils";
import {
  CodApprovalStatus,
  CodInvoice,
  CodInvoiceAllocation,
  CodInvoicePaymentResult,
  CodInvoiceSelection,
  CodOrder,
  CodOrderInvoices,
  CodOrderItem,
  CodOrderLine,
  CodPaymentTransaction,
  CodPostOrderResult,
  CodPostPaymentRequest,
  CodPostPaymentResponse,
  CodPostSkippedOrFailed,
  CodRetryResponse,
  CodReviewStatus,
} from "../types/codSalesRep";

export const parseApprovalStatus = (raw: unknown): CodApprovalStatus => {
  if (typeof raw === "number" && raw >= 0 && raw <= 3) return raw as CodApprovalStatus;
  const text = String(raw ?? "").toLowerCase();
  if (text === "pendingapproval" || text === "1") return 1;
  if (text === "approved" || text === "2") return 2;
  if (text === "rejected" || text === "3") return 3;
  const numeric = Number(raw);
  if (numeric === 1 || numeric === 2 || numeric === 3) return numeric as CodApprovalStatus;
  return 0;
};

export const deriveCodReviewStatus = (order: CodOrder): CodReviewStatus => {
  if (isFullyPaidCodOrder(order)) return "paid";
  const paymentStatus = String(order.paymentStatus ?? "").toUpperCase().replace(/-/g, "_");
  if (
    paymentStatus === "PARTIALLY_PAID" ||
    paymentStatus === "PARTIALLYPAID" ||
    (Number(order.amountPaid ?? 0) > 0 && !isFullyPaidCodOrder(order))
  ) {
    return "partlyPaid";
  }
  if (order.approvalStatus === 3 || order.rejectionReason) return "rejected";
  if (order.approvalStatus === 1) return "pendingReview";
  if (order.approvalStatus === 2) {
    const sage = order.salesOrderNumber || (order.sageOrderNumbers?.length ?? 0) > 0;
    if (order.isSynced || sage) return "postedToSage";
    return "approved";
  }
  if (order.reviewStatus) return order.reviewStatus;
  return "pendingReview";
};

const normalizeOrderItems = (itemsRaw: unknown): CodOrderItem[] => {
  if (!Array.isArray(itemsRaw)) return [];
  return itemsRaw.map((item) => {
    const row = item as Record<string, unknown>;
    const quantity = Number(pickField(row, "quantity", "Quantity") ?? 0);
    const price = Number(pickField(row, "price", "Price") ?? 0);
    const grossPrice = Number(
      pickField(row, "grossPrice", "GrossPrice") ?? price * quantity
    );
    return {
      productId: String(pickField(row, "productId", "ProductId") ?? ""),
      code: String(pickField(row, "code", "Code") ?? ""),
      name: String(pickField(row, "name", "Name") ?? ""),
      quantity,
      price,
      grossPrice: grossPrice > 0 ? grossPrice : price * quantity,
    };
  });
};

const toNumber = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

export const normalizeCodPayment = (
  raw: Record<string, unknown>
): CodPaymentTransaction => ({
  id: String(pickField(raw, "id", "Id") ?? ""),
  transactionId: String(pickField(raw, "transactionId", "TransactionId") ?? ""),
  orderId: String(pickField(raw, "orderId", "OrderId") ?? ""),
  invoiceId: String(pickField(raw, "invoiceId", "InvoiceId") ?? ""),
  amount: Number(pickField(raw, "amount", "Amount") ?? 0),
  currency: String(pickField(raw, "currency", "Currency") ?? "IQD"),
  status: String(pickField(raw, "status", "Status") ?? ""),
  documentReceiptNumber: String(
    pickField(raw, "documentReceiptNumber", "DocumentReceiptNumber") ?? ""
  ),
  sagePaymentNumber: String(
    pickField(raw, "sagePaymentNumber", "SagePaymentNumber") ?? ""
  ),
  isSynced: Boolean(pickField(raw, "isSynced", "IsSynced") ?? false),
  paymentDate: pickField(raw, "paymentDate", "PaymentDate") as string | undefined,
  customerId: String(pickField(raw, "customerId", "CustomerId") ?? ""),
  customerCode: String(pickField(raw, "customerCode", "CustomerCode") ?? ""),
  isPartialPayment: Boolean(
    pickField(raw, "isPartialPayment", "IsPartialPayment") ?? false
  ),
  orderTotal: toNumber(pickField(raw, "orderTotal", "OrderTotal")),
  remainingAfterPayment: toNumber(
    pickField(raw, "remainingAfterPayment", "RemainingAfterPayment")
  ),
  paymentReference: String(pickField(raw, "paymentReference", "PaymentReference") ?? "").trim(),
  invoices: asRecordArray(pickField(raw, "invoices", "Invoices")).map(
    (row): CodInvoiceAllocation => ({
      invoiceId: String(pickField(row, "invoiceId", "InvoiceId") ?? ""),
      invoiceNumber: String(pickField(row, "invoiceNumber", "InvoiceNumber") ?? ""),
      subOrderReference: String(pickField(row, "subOrderReference", "SubOrderReference") ?? ""),
      amount: Number(pickField(row, "amount", "Amount") ?? 0),
    })
  ),
});

const asRecordArray = (raw: unknown): Record<string, unknown>[] =>
  Array.isArray(raw) ? raw.map((row) => (row ?? {}) as Record<string, unknown>) : [];

const optionalDate = (raw: unknown): string | undefined => {
  const value = typeof raw === "string" ? raw.trim() : "";
  return value && !value.startsWith("0001") ? value : undefined;
};

/* ── COD invoices: Order → Line → Sage invoice ─────────────────────────────── */

export const normalizeCodInvoice = (raw: Record<string, unknown>): CodInvoice => ({
  invoiceId: String(pickField(raw, "invoiceId", "InvoiceId") ?? ""),
  invoiceNumber: String(pickField(raw, "invoiceNumber", "InvoiceNumber") ?? ""),
  invoiceDate: optionalDate(pickField(raw, "invoiceDate", "InvoiceDate")),
  dueDate: optionalDate(pickField(raw, "dueDate", "DueDate")),
  amount: Number(pickField(raw, "amount", "Amount") ?? 0),
  paidAmount: Number(pickField(raw, "paidAmount", "PaidAmount") ?? 0),
  appPaidAmount: Number(pickField(raw, "appPaidAmount", "AppPaidAmount") ?? 0),
  remainingAmount: Number(pickField(raw, "remainingAmount", "RemainingAmount") ?? 0),
  paymentStatus: String(pickField(raw, "paymentStatus", "PaymentStatus") ?? ""),
  isPayable: Boolean(pickField(raw, "isPayable", "IsPayable") ?? false),
  notPayableReason: String(pickField(raw, "notPayableReason", "NotPayableReason") ?? ""),
  pendingTransactionId:
    (pickField(raw, "pendingTransactionId", "PendingTransactionId") as string | null | undefined) ?? null,
});

export const normalizeCodOrderInvoices = (raw: Record<string, unknown>): CodOrderInvoices => ({
  orderId: String(pickField(raw, "orderId", "OrderId") ?? ""),
  orderNumber: String(pickField(raw, "orderNumber", "OrderNumber") ?? ""),
  orderDate: optionalDate(pickField(raw, "orderDate", "OrderDate")),
  customerId: String(pickField(raw, "customerId", "CustomerId") ?? ""),
  customerCode: String(pickField(raw, "customerCode", "CustomerCode") ?? ""),
  customerName: String(pickField(raw, "customerName", "CustomerName") ?? ""),
  approvalStatus: String(pickField(raw, "approvalStatus", "ApprovalStatus") ?? ""),
  currency: String(pickField(raw, "currency", "Currency") ?? "IQD") || "IQD",
  orderTotal: Number(pickField(raw, "orderTotal", "OrderTotal") ?? 0),
  totalInvoiced: Number(pickField(raw, "totalInvoiced", "TotalInvoiced") ?? 0),
  totalRemaining: Number(pickField(raw, "totalRemaining", "TotalRemaining") ?? 0),
  hasPayableInvoice: Boolean(pickField(raw, "hasPayableInvoice", "HasPayableInvoice") ?? false),
  // Lines are kept in the order the API gives them.
  lines: asRecordArray(pickField(raw, "lines", "Lines")).map(
    (line): CodOrderLine => ({
      reference: String(pickField(line, "reference", "Reference") ?? ""),
      category: String(pickField(line, "category", "Category") ?? ""),
      lineTotal: Number(pickField(line, "lineTotal", "LineTotal") ?? 0),
      hasInvoice: Boolean(pickField(line, "hasInvoice", "HasInvoice") ?? false),
      invoices: asRecordArray(pickField(line, "invoices", "Invoices")).map(normalizeCodInvoice),
    })
  ),
});

export const normalizeCodOrder = (raw: Record<string, unknown>): CodOrder => {
  const items = normalizeOrderItems(pickField(raw, "items", "Items"));
  const sageRefs = parseSageOrderNumbers(
    pickField(raw, "sageOrderNumbers", "SageOrderNumbers")
  );
  const salesOrderNumber = String(
    pickField(raw, "salesOrderNumber", "SalesOrderNumber") ?? sageRefs.join(", ")
  ).trim();
  if (salesOrderNumber && sageRefs.length === 0) {
    sageRefs.push(...parseSageOrderNumbers(salesOrderNumber));
  }
  const approvalStatus = parseApprovalStatus(
    pickField(raw, "approvalStatus", "ApprovalStatus")
  );
  const number = String(pickField(raw, "number", "Number") ?? "");
  const payableTotal =
    toNumber(pickField(raw, "payableTotal", "PayableTotal")) ??
    computeOrderAmount(raw, items);
  const amountPaid = toNumber(pickField(raw, "amountPaid", "AmountPaid")) ?? 0;
  const outstandingAmount =
    toNumber(pickField(raw, "outstandingAmount", "OutstandingAmount")) ??
    Math.max(0, payableTotal - amountPaid);

  const order: CodOrder = {
    id: String(pickField(raw, "id", "Id") ?? ""),
    number,
    customerId: String(pickField(raw, "customerId", "CustomerId") ?? ""),
    customerName: String(pickField(raw, "customerName", "CustomerName") ?? ""),
    customerCode: String(pickField(raw, "customerCode", "CustomerCode") ?? ""),
    amount: computeOrderAmount(raw, items),
    currency: String(pickField(raw, "currency", "Currency") ?? "IQD"),
    paymentMethod: String(pickField(raw, "paymentMethod", "PaymentMethod") ?? ""),
    paymentStatus: String(pickField(raw, "paymentStatus", "PaymentStatus") ?? ""),
    isSynced: Boolean(pickField(raw, "isSynced", "IsSynced") ?? false),
    date: String(pickField(raw, "date", "Date") ?? ""),
    status: String(pickField(raw, "status", "Status") ?? ""),
    sageOrderNumbers: sageRefs,
    salesOrderNumber: salesOrderNumber || sageRefs.join(", ") || undefined,
    items,
    // The API sends the rep's name as createdByRepName; salesRepName kept for older payloads.
    salesRepName: String(
      pickField(raw, "salesRepName", "SalesRepName", "createdByRepName", "CreatedByRepName") ?? ""
    ),
    salesRepId: String(
      pickField(raw, "salesRepId", "SalesRepId", "createdByRepId", "CreatedByRepId") ?? ""
    ),
    createdByRepId: String(pickField(raw, "createdByRepId", "CreatedByRepId") ?? ""),
    orderChannel: pickField(raw, "orderChannel", "OrderChannel") as string | number | undefined,
    approvalStatus,
    approvalStatusName: approvalStatusName(approvalStatus),
    rejectionReason:
      String(pickField(raw, "rejectionReason", "RejectionReason") ?? "") || undefined,
    submittedAt:
      String(pickField(raw, "submittedAt", "SubmittedAt", "date", "Date") ?? "") ||
      undefined,
    amountPaid,
    payableTotal,
    outstandingAmount,
    firstPaymentAt:
      String(pickField(raw, "firstPaymentAt", "FirstPaymentAt") ?? "") || undefined,
    fullyPaidAt:
      String(pickField(raw, "fullyPaidAt", "FullyPaidAt") ?? "") || undefined,
    paymentNumber:
      String(pickField(raw, "paymentNumber", "PaymentNumber") ?? "") || undefined,
    isDeleted: Boolean(pickField(raw, "isDeleted", "IsDeleted") ?? false),
  };

  order.reviewStatus = deriveCodReviewStatus(order);
  return order;
};

const parseArray = <T>(
  data: unknown,
  normalizer: (row: Record<string, unknown>) => T
): T[] => unwrapListPayload(data).map((row) => normalizer(row));

const axiosStatus = (error: unknown) =>
  error instanceof AxiosError ? error.response?.status : undefined;

const sortOrdersByDate = (orders: CodOrder[]) =>
  [...orders].sort(
    (a, b) =>
      new Date(b.date || b.submittedAt || 0).getTime() -
      new Date(a.date || a.submittedAt || 0).getTime()
  );

const mergeOrdersById = (...groups: CodOrder[][]): CodOrder[] => {
  const map = new Map<string, CodOrder>();
  groups.flat().forEach((order) => {
    if (order.id) map.set(order.id, order);
  });
  return sortOrdersByDate(Array.from(map.values()));
};

const parseSkippedOrFailed = (raw: unknown): CodPostSkippedOrFailed[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map((row) => {
    const item = (row ?? {}) as Record<string, unknown>;
    return {
      orderId: String(pickField(item, "orderId", "OrderId") ?? ""),
      orderNumber: String(pickField(item, "orderNumber", "OrderNumber") ?? ""),
      invoiceNumbers: String(pickField(item, "invoiceNumbers", "InvoiceNumbers") ?? ""),
      reason: String(pickField(item, "reason", "Reason") ?? ""),
      message: String(pickField(item, "message", "Message") ?? ""),
    };
  });
};

const parsePostOrderResults = (raw: unknown): CodPostOrderResult[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map((row) => {
    const item = (row ?? {}) as Record<string, unknown>;
    return {
      orderId: String(pickField(item, "orderId", "OrderId") ?? ""),
      orderNumber: String(pickField(item, "orderNumber", "OrderNumber") ?? ""),
      orderTotal: Number(pickField(item, "orderTotal", "OrderTotal") ?? 0),
      amountCollected: toNumber(
        pickField(item, "amountCollected", "AmountCollected")
      ),
      amountPaid: Number(pickField(item, "amountPaid", "AmountPaid") ?? 0),
      remainingAmount: Number(
        pickField(item, "remainingAmount", "RemainingAmount") ?? 0
      ),
      paymentStatus: String(pickField(item, "paymentStatus", "PaymentStatus") ?? ""),
      transactionId: String(pickField(item, "transactionId", "TransactionId") ?? ""),
      paymentReference: String(pickField(item, "paymentReference", "PaymentReference") ?? ""),
      invoices: asRecordArray(pickField(item, "invoices", "Invoices")).map(
        (inv): CodInvoicePaymentResult => ({
          invoiceId: String(pickField(inv, "invoiceId", "InvoiceId") ?? ""),
          invoiceNumber: String(pickField(inv, "invoiceNumber", "InvoiceNumber") ?? ""),
          subOrderReference: String(pickField(inv, "subOrderReference", "SubOrderReference") ?? ""),
          amountPaidNow: Number(pickField(inv, "amountPaidNow", "AmountPaidNow") ?? 0),
          remainingAmount: Number(pickField(inv, "remainingAmount", "RemainingAmount") ?? 0),
          paymentStatus: String(pickField(inv, "paymentStatus", "PaymentStatus") ?? ""),
        })
      ),
    };
  });
};

export interface CodInvoiceQuery {
  customerId?: string;
  orderIds?: string[];
  includePaid?: boolean;
}

export interface CodOutstandingQuery {
  customerId?: string;
  customerCode?: string;
  salesRepId?: string;
}

class CodService {
  private async withCodFallback<T>(
    primary: () => Promise<T>,
    fallback: () => Promise<T>
  ): Promise<T> {
    try {
      return await primary();
    } catch (error) {
      if (isCodRouteMissingStatus(axiosStatus(error))) return fallback();
      throw error;
    }
  }

  private async fetchOrdersByIds(ids: string[]): Promise<CodOrder[]> {
    const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
    if (uniqueIds.length === 0) return [];

    const batchSize = 5;
    const fetched: CodOrder[] = [];
    for (let i = 0; i < uniqueIds.length; i += batchSize) {
      const batch = uniqueIds.slice(i, i + batchSize);
      const results = await Promise.allSettled(
        batch.map(async (id) => {
          const res = await httpTokenCommon.get(`/orders/${id}`, { silent: true });
          return normalizeCodOrder((res.data ?? {}) as Record<string, unknown>);
        })
      );
      results.forEach((result) => {
        if (result.status === "fulfilled" && result.value.id) {
          fetched.push(result.value);
        }
      });
    }
    return fetched;
  }

  /** Raw COD payment transactions (GET /api/cod), without order enrichment. Used by reports. */
  async listPaymentTransactions(): Promise<CodPaymentTransaction[]> {
    const fetch = async (path: string) => {
      const res = await httpTokenCommon.get(path);
      return parseArray(res.data, normalizeCodPayment);
    };
    return this.withCodFallback(
      () => fetch("/cod"),
      () => fetch("/payments/cod")
    );
  }

  async listPayments(): Promise<EnrichedCodPayment[]> {
    const fetch = async (path: string) => {
      const res = await httpTokenCommon.get(path);
      return parseArray(res.data, normalizeCodPayment);
    };
    const payments = await this.withCodFallback(
      () => fetch("/cod"),
      () => fetch("/payments/cod")
    );

    let mine: CodOrder[] = [];
    try {
      mine = await this.listOrders();
    } catch {
      mine = [];
    }
    const missingIds = payments
      .map((p) => p.orderId)
      .filter((id) => id && !mine.some((order) => order.id === id));
    const extra = await this.fetchOrdersByIds(missingIds);
    const orders = mergeOrdersById(mine, extra);
    const lookup = new Map(
      orders.map((order) => [
        order.customerId,
        { code: order.customerCode ?? "", name: order.customerName ?? "" },
      ])
    );
    return enrichCodPayments(payments, orders, lookup);
  }

  /**
   * POST /api/cod/post — a cash collection paid against Sage invoices (never by order).
   * Leave an invoice's amount out to pay its whole remainingAmount.
   */
  async postPayment(payload: CodPostPaymentRequest): Promise<CodPostPaymentResponse> {
    const body = {
      documentReceiptNumber: payload.documentReceiptNumber,
      invoices: payload.invoices.map((selection) =>
        selection.amount != null && Number.isFinite(selection.amount) && selection.amount > 0
          ? { invoiceId: selection.invoiceId, amount: selection.amount }
          : { invoiceId: selection.invoiceId }
      ),
    };
    const res = await httpTokenCommon.post("/cod/post", body);
    const data = (res.data ?? {}) as Record<string, unknown>;
    const ids = pickField(data, "transactionIds", "TransactionIds");
    return {
      transactionIds: Array.isArray(ids) ? ids.map(String) : [],
      skipped: parseSkippedOrFailed(pickField(data, "skipped", "Skipped")),
      failed: parseSkippedOrFailed(pickField(data, "failed", "Failed")),
      orders: parsePostOrderResults(pickField(data, "orders", "Orders")),
    };
  }

  /**
   * GET /api/cod/invoices — COD orders with their lines and each line's Sage invoices.
   * Reps see their assigned customers only; managers see all customers.
   */
  async listInvoiceOrders(query: CodInvoiceQuery = {}): Promise<CodOrderInvoices[]> {
    const params: Record<string, string> = {
      includePaid: query.includePaid ? "true" : "false",
    };
    if (query.customerId) params.customerId = query.customerId;
    if (query.orderIds && query.orderIds.length > 0) params.orderIds = query.orderIds.join(",");
    const res = await httpTokenCommon.get("/cod/invoices", { params });
    return parseArray(res.data, normalizeCodOrderInvoices);
  }

  /**
   * POST /api/payments/cod/invoices/pay — customer pays COD invoices online (one order only).
   * method: ZainCash | CardPayment | QiCard (Wallet is refused). The response has the same shape
   * as the other gateway create calls, so the existing redirect / polling / cancel flow applies.
   */
  async payMyCodInvoices(body: {
    invoices: CodInvoiceSelection[];
    method: string;
    language?: string;
    successUrl?: string;
    failedUrl?: string;
    redirectUrl?: string;
    externalReferenceId?: string;
  }): Promise<Record<string, unknown>> {
    const res = await httpTokenCommon.post("/payments/cod/invoices/pay", body);
    return (res.data ?? {}) as Record<string, unknown>;
  }

  /** GET /api/payments/cod/invoices — the logged-in customer's COD orders → lines → invoices. */
  async listMyCodInvoices(query: { orderId?: string; includePaid?: boolean } = {}): Promise<CodOrderInvoices[]> {
    const params: Record<string, string> = { includePaid: query.includePaid ? "true" : "false" };
    if (query.orderId) params.orderId = query.orderId;
    const res = await httpTokenCommon.get("/payments/cod/invoices", { params });
    return parseArray(res.data, normalizeCodOrderInvoices);
  }

  async retryPayment(transactionId: string): Promise<CodRetryResponse> {
    const retry = async (path: string): Promise<CodRetryResponse> => {
      try {
        const res = await httpTokenCommon.post(path, undefined, { silent: true });
        const data = (res.data ?? {}) as Record<string, unknown>;
        return {
          message: String(pickField(data, "message", "Message") ?? ""),
          transactionId: String(
            pickField(data, "transactionId", "TransactionId") ?? transactionId
          ),
        };
      } catch (error) {
        if (isInvoiceNotIssuedError(error)) {
          const data = ((error as AxiosError).response?.data ?? {}) as Record<
            string,
            unknown
          >;
          return {
            message: String(
              pickField(data, "message", "Message") ??
                "Sage has not issued the invoice yet. This receipt will post automatically."
            ),
            transactionId,
            error: "INVOICE_NOT_ISSUED",
            invoiceNotIssued: true,
          };
        }
        throw error;
      }
    };
    return this.withCodFallback(
      () => retry(`/cod/retry/${transactionId}`),
      () => retry(`/payments/cod/retry/${transactionId}`)
    );
  }

  /** GET /api/orders/cod/mine — SalesRep's COD orders */
  async listOrders(): Promise<CodOrder[]> {
    const res = await httpTokenCommon.get("/orders/cod/mine");
    const rows = parseArray(res.data, normalizeCodOrder);
    return enrichCodOrders(rows);
  }

  /**
   * GET /api/orders/cod/outstanding-by-customer
   * Falls back to aggregating /cod/mine when the endpoint is unavailable.
   */
  async getOutstandingByCustomer(
    params?: CodOutstandingQuery
  ): Promise<OutstandingApiResult & { fromFallback: boolean }> {
    try {
      const res = await httpTokenCommon.get("/orders/cod/outstanding-by-customer", {
        params,
        silent: true,
      });
      const payload = normalizeOutstandingApiPayload(res.data);
      return { ...payload, fromFallback: false };
    } catch (error) {
      const status = axiosStatus(error);
      if (isCodRouteMissingStatus(status)) {
        const orders = await this.listOrders();
        const items = buildCustomerOutstandingSummaries(orders);
        const scoped = params?.customerId
          ? items.filter((row) => row.customerId === params.customerId)
          : params?.customerCode
            ? items.filter((row) => row.customerCode === params.customerCode)
            : items;
        return {
          currency: "IQD",
          totalOutstandingAmount: scoped.reduce(
            (sum, row) => sum + row.outstandingAmount,
            0
          ),
          totalUnpaidOrderCount: scoped.reduce(
            (sum, row) => sum + row.unpaidOrderCount,
            0
          ),
          items: scoped,
          fromFallback: true,
        };
      }
      throw error;
    }
  }

  /** Approved + not fully paid — instalments stay postable. */
  async listUnpaidOrders(): Promise<CodOrder[]> {
    const orders = await this.listOrders();
    return orders.filter(isPostableCodOrder);
  }

  /** One manager bucket, normalised but not enriched (callers enrich once over the merged list). */
  private async fetchManagerBucket(path: string): Promise<CodOrder[]> {
    try {
      const res = await httpTokenCommon.get(path, { silent: true });
      return parseArray(res.data, normalizeCodOrder);
    } catch {
      return [];
    }
  }

  private async listManagerBucket(path: string): Promise<CodOrder[]> {
    return enrichCodOrders(await this.fetchManagerBucket(path));
  }

  /** GET /api/orders/cod/pending — SalesManager pending approvals */
  async listPendingCodOrders(): Promise<CodOrder[]> {
    return this.listManagerBucket("/orders/cod/pending");
  }

  /** GET /api/orders/cod/approved — SalesManager approved orders */
  async listApprovedCodOrders(): Promise<CodOrder[]> {
    return this.listManagerBucket("/orders/cod/approved");
  }

  /** GET /api/orders/cod/rejected — SalesManager rejected orders */
  async listRejectedCodOrders(): Promise<CodOrder[]> {
    return this.listManagerBucket("/orders/cod/rejected");
  }

  /** @deprecated use listPendingCodOrders */
  async listApprovalOrders(): Promise<CodOrder[]> {
    return this.listPendingCodOrders();
  }

  /** Merge pending + approved + rejected; a failed bucket is treated as empty. */
  async listManagerOrders(): Promise<CodOrder[]> {
    // Three buckets in parallel, then ONE enrichment pass over the merged list — customer
    // details are resolved once per customer instead of once per bucket.
    const [pending, approved, rejected] = await Promise.all([
      this.fetchManagerBucket("/orders/cod/pending"),
      this.fetchManagerBucket("/orders/cod/approved"),
      this.fetchManagerBucket("/orders/cod/rejected"),
    ]);
    return enrichCodOrders(mergeOrdersById(pending, approved, rejected));
  }


  async approveOrder(orderId: string): Promise<CodOrder> {
    const res = await httpTokenCommon.post(`/orders/cod/${orderId}/approve`, undefined, {
      silent: true,
      timeout: 20000,
    });
    const order = normalizeCodOrder((res.data ?? {}) as Record<string, unknown>);
    try {
      const [enriched] = await enrichCodOrders([order]);
      return enriched;
    } catch {
      return order;
    }
  }

  async rejectOrder(orderId: string, reason: string): Promise<CodOrder> {
    const res = await httpTokenCommon.post(
      `/orders/cod/${orderId}/reject`,
      { reason },
      { silent: true }
    );
    const order = normalizeCodOrder((res.data ?? {}) as Record<string, unknown>);
    const [enriched] = await enrichCodOrders([order]);
    return enriched;
  }
}

export { isPostableCodOrder };
const codService = new CodService();
export default codService;
